import qs from 'qs';

import url from 'url';

import { getI18nLocale, translate } from '@front/i18n';

import { isSuccessful } from '../utils/http-status';
import { getCurrentCountry } from '../utils/country';

import { JsonResponse, RequestOptions } from './types';
import ApiError from './api-error';

type Headers = Record<string, string>;

let baseUrl: string;
let headers: Headers;
let authorizationToken: string | null;

const t = translate('alerts');

export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/*
 * errors.payment and errors.potential are specific case for createPotential API
 */
const errorMessage = (body: JsonResponse): string => {
  if (body.message) {
    return body.message;
  }
  if (body.errors) {
    if (body.errors instanceof Array) {
      return body.errors.join('. ');
    }
    if (body.errors.payment) {
      return body.errors.payment;
    }
    if (body.errors.potential) {
      return body.errors.potential.join('. ');
    }
    return String(body.errors);
  }
  if (body.error) {
    return body.error;
  }
  return t('.unknown_api_error.message_with_detail', { detail: JSON.stringify(body) });
};

// We export this function to test that it works as expected
export const formatUrl = (mainUrl: string, pathname: string, options?: RequestOptions) => {
  const baseConfig = url.parse(mainUrl);
  return url.format({
    auth: baseConfig.auth,
    hostname: baseConfig.hostname,
    port: baseConfig.port,
    protocol: baseConfig.protocol,
    pathname: baseConfig.pathname + pathname,
    search:
      options?.search ||
      qs.stringify(options?.query, {
        arrayFormat: 'brackets',
      }),
  });
};

type BuildHeadersParams = {
  isFormData: boolean;
  authorizationTokenOverride?: string;
  isFormDataInWebContext: boolean;
};

const buildHeaders = ({
  isFormData,
  authorizationTokenOverride,
  isFormDataInWebContext,
}: BuildHeadersParams): Headers => {
  const finalAuthToken = authorizationTokenOverride || authorizationToken;
  const baseHeaders: Headers = {
    ...headers,
    'Accept-Language': getI18nLocale(),
    'Wecasa-Country': getCurrentCountry(),
    'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
  };

  if (isFormDataInWebContext) {
    // This is needed for FormData requests (e.g. chat messages):
    // When using React Native's fetch implementation for FormData, Content-Type: 'multipart/form-data' needs to be
    // set as header and the request body as FormData
    // But the web's fetch implementation requires to not set the Content-Type header explicitly as otherwise
    // the browser cannot set the needed boundary (leads to HTTP 422 requests on our server)
    delete baseHeaders['Content-Type'];
  }

  return finalAuthToken ? { ...baseHeaders, authorization: finalAuthToken } : baseHeaders;
};

const getBody = (options: RequestOptions) =>
  options.isFormData ? (options.body as FormData) : JSON.stringify(options.body);

const makeRequest = async (method: string, requestUrl: string, options: RequestOptions = {}) => {
  try {
    const path = !options.madeFromServer ? formatUrl(options.baseUrl || baseUrl, requestUrl, options) : requestUrl;
    const response = await fetch(path, {
      method,
      headers: buildHeaders({
        isFormData: !!options.isFormData,
        authorizationTokenOverride: options.authorizationTokenOverride,
        isFormDataInWebContext: !!options.isFormDataInWebContext,
      }),
      body: getBody(options),
    });
    if (isSuccessful(response.status)) {
      if (options.headersOnly) return response.headers;
      if (options.skipJsonParsing) return response;

      // If the response has no body content (e.g. 204 No Content), we don't want to read the body stream as json()
      // otherwise it would throw an error.
      // Reading the body as text and checking its length is a workaround for this issue.
      const responseBody = await response.text();
      if (responseBody.length === 0) {
        return response;
      }

      return JSON.parse(responseBody);
    }

    // Read the response body once and store it
    const responseBody = await response.text();

    let body;
    try {
      // Backend does not always return a json response with an error status
      body = JSON.parse(responseBody);
    } catch (error) {
      body = { errors: [t('.unknown_api_error.message')] };
    }

    const message = errorMessage(body);

    throw new ApiError(
      message,
      response.status,
      body.errors instanceof Array || typeof body.errors === 'object' ? body.errors : [message],
      body,
    );
  } catch (error) {
    // For standard errors like parse errors we add the errors attribute so it is always present
    if (error instanceof Error && !(error as any).errors && !(error instanceof ApiError)) {
      if (error.message.includes('Network request failed')) {
        error.message = t('.network_error.message');
      } else if (
        error.message.includes('Unexpected end of JSON input') ||
        error.message.includes('Unexpected token')
      ) {
        error.message = t('.unknown_api_error.message');
      }
      throw Object.assign(error, { errors: [error.message] });
    }
    throw error;
  }
};

export const init = (baseUrlValue: string, userAgent?: string, deviceUuid?: string) => {
  baseUrl = baseUrlValue;
  headers = userAgent
    ? {
        ...DEFAULT_HEADERS,
        'User-Agent': userAgent,
      }
    : {
        ...DEFAULT_HEADERS,
      };

  if (deviceUuid) {
    headers['Wecasa-Device'] = deviceUuid;
  }
};

export const setAuthorizationToken = (token: string | null) => {
  authorizationToken = token;
};

export const getAuthorizationToken = () => authorizationToken;

export const get = (requestUrl: string, options: RequestOptions = {}) => makeRequest('GET', requestUrl, options);

export const post = (requestUrl: string, options: RequestOptions = {}) => makeRequest('POST', requestUrl, options);

export const put = (requestUrl: string, options: RequestOptions = {}) => makeRequest('PUT', requestUrl, options);
export const patch = (requestUrl: string, options: RequestOptions = {}) => makeRequest('PATCH', requestUrl, options);

export const del = (requestUrl: string, options: RequestOptions = {}) => makeRequest('DELETE', requestUrl, options);

export const fetchWithTimeout = async (requestUrl: RequestInfo, options: RequestInit & { timeout?: number } = {}) => {
  const { timeout = 8000, ...requestOptions } = options;

  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
  }, timeout);
  const response = await fetch(requestUrl, {
    ...requestOptions,
    signal: controller.signal,
  });
  clearTimeout(id);
  return response;
};
